<template>
  <VForm autocomplete="off" @submit.prevent>
    <VRow v-if="!isEditMode">
      <VCol
        cols="12"
        sm="6"
        md="12"
        class="pb-1"
      >
        <VRow no-gutters>
          <VCol cols="12" md="8">
            <div class="candidate-name-row">
              <span class="display-1 font-weight-light">
                {{ candidate.lastName }} {{ candidate.firstName }}
              </span>
              <VBtn
                v-if="!readonly"
                text
                icon
                x-small
                class="ml-1"
                @click="switchToEditMode"
              >
                <VIcon>mdi-pencil</VIcon>
              </VBtn>
            </div>
            <div
              v-if="hhStateName"
              class="caption grey--text darken-1--text"
            >
              {{ $t('hhStateLabel') }}: {{ hhStateName }}
            </div>
            <div
              v-if="candidate.birthDate"
              class="pt-1 caption grey--text darken-1--text"
            >
              {{ birthDate }}
            </div>
          </VCol>
          <VCol
            cols="12"
            md="4"
            :class="{ 'text-right': $vuetify.breakpoint.mdAndUp, 'd-flex justify-space-between align-center': $vuetify.breakpoint.smAndDown }"
          >
            <CandidateRating
              :candidate="candidate"
              :readonly="readonly"
              :align="$vuetify.breakpoint.mdAndUp ? 'align-end': 'align-start'"
              description
              size="16"
            />
            <div v-if="hideBlockRatingComputed" />
            <div v-else class="d-flex justify-end mt-1">
              <div class="ai-text-review">
                <template
                  v-if="isAiRatingAvailable"
                >
                  <span>{{ candidateRating }}/5</span>
                  {{ $t('accordingAi') }}
                </template>

                <template v-else-if="isProcessingAi">
                  {{ $t('evAiIsProcessing') }}
                </template>

                <template v-else-if="isAvailableAi">
                  {{ $t('availableAi') }}
                </template>

                <template v-else-if="isNotAvailableAi">
                  {{ $t('notAvailableAi') }}
                </template>
              </div>

              <VMenu
                offset-y
                open-on-click
                left
                nudge-right="25"
                transition="scale-transition"
                origin="right top"
              >
                <template v-slot:activator="{ on, attrs }">
                  <div
                    class="icon-more-detailed"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <img :src="require('@/assets/img/info.svg')" alt="trash">
                  </div>
                </template>
                <template>
                  <VCard class="error__trial">
                    <div class="d-flex">
                      <div v-if="!account.aiReview || isExpired" class="mr-2">
                        <img
                          :src="require('@/assets/img/trial.svg')"
                          class="error__trial_icon"
                          alt="trash"
                        >
                      </div>
                      <div class="flex-column">
                        <VCol
                          class="pa-0"
                          cols="12"
                          lg="12"
                          md="8"
                        >
                          <div class="error__title">
                            <template v-if="isExpired">
                              {{ $t('isExpiredTrialTitle') }}
                            </template>
                            <template v-else-if="!account.aiReview">
                              {{ $t('conversionIsAvailable') }}
                            </template>
                            <template v-else>
                              <div class="mb-2">
                                {{ $t('textTooltipAi.1') }}
                              </div>
                              <span>
                                {{ $t('textTooltipAi.2') }}
                              </span>
                            </template>
                          </div>
                        </VCol>
                        <VCol
                          v-if="!account.aiReview || isExpired"
                          cols="12"
                          lg="4"
                          class="error__trial_links pa-0 mt-2"
                        >
                          <div
                            v-if="!isExpired"
                            class="pa-0 text-end try-days-block"
                            cols="12"
                            lg="2"
                          >
                            <RouterLink
                              class="button-trial"
                              :to="{ name: 'settings',
                                     params: { type: settingsTabs.PAYMENT, addType: paymentTabs.PLANS } }"
                            >
                              {{ $t('free') }}
                            </RouterLink>
                          </div>
                          <div
                            class="pa-0 text-center change-tariff-block"
                            cols="12"
                            lg="2"
                          >
                            <RouterLink
                              class="button-tariff"
                              :to="isExpired ? { name: 'settings', params: { type: settingsTabs.PAYMENT, addType: paymentTabs.INFO } } : { name: 'settings', params: { type: settingsTabs.PAYMENT, addType: paymentTabs.PLANS } }"
                            >
                              <span v-if="isExpired">{{ $t('payTheTariff') }}</span>
                              <span v-else>{{ $t('changeTariff') }}</span>
                            </RouterLink>
                          </div>
                        </VCol>
                      </div>
                      <VBtn
                        class="error__trial_close"
                        fab
                        icon
                        x-small
                      >
                        <VIcon>
                          mdi-close
                        </VIcon>
                      </VBtn>
                    </div>
                  </VCard>
                </template>
              </VMenu>
            </div>
          </VCol>
        </VRow>
        <VRow no-gutters>
          <VCol cols="12" class="grey--text darken-1--text body-2 pt-2">
            <CandidateStatusPanel :candidate="candidate" :readonly="readonly" />
          </VCol>
        </VRow>
      </VCol>
      <VCol
        cols="12"
        sm="6"
        md="12"
        class="grey--text darken-1--text body-2 py-1"
      >
        <VRow no-gutters class="flex-wrap flex-sm-nowrap pt-sm-12 pt-md-0">
          <VCol class="col-12 col-sm-7">
            <div v-if="candidate.location || candidate.birthDate">
              <CandidateLocationAndYearsOld :candidate="candidate" />
            </div>
            <div v-if="candidate.email">
              <a
                :href="'mailto:' + candidate.email"
                style="word-break: break-word"
              >{{ candidate.email }}</a>
            </div>
            <div v-if="candidate.phone">
              {{ candidate.phone }} <a style="text-decoration:none" @click="openWhatsApp">
                <VIcon color="green" small>mdi-whatsapp</VIcon>
              </a>
            </div>
          </VCol>
          <VCol class="col-12 col-sm-5 text-sm-right">
            <VChip
              color="#fff"
              link
              :disabled="!candidate.resume"
              class="px-0 px-md-2"
              @click="downloadResume"
            >
              <VIcon small class="mr-2">
                mdi-download
              </VIcon>
              <span
                class="text-decoration-underline"
                :class="{ 'primary--text': candidate.resume }"
              >
                <template v-if="$vuetify.breakpoint.mdAndUp">
                  {{ $t('downloadResume') }}
                </template>
                <template v-else>
                  {{ $t('resume') }}
                </template>
              </span>
            </VChip>
            <VMenu
              v-model="shareMe"
              offset-y
              :close-on-content-click="false"
              z-index="500"
              min-width="320"
              max-width="500"
            >
              <template v-slot:activator="{ on }">
                <VChip
                  color="#fff"
                  link
                  class="px-0 px-md-2"
                  v-on="on"
                  @click="generateCandidateUrl"
                >
                  <VIcon small class="mr-2">
                    mdi-share
                  </VIcon>
                  <span
                    class="text-decoration-underline primary--text"
                  >
                    <template v-if="$vuetify.breakpoint.mdAndUp">
                      {{ $t('sendToCollegue') }}
                    </template>
                    <template v-else>
                      {{ $t('send') }}
                    </template>
                  </span>
                </VChip>
              </template>
              <VCard tile class="pr-10" style="box-shadow: none;">
                <VBtn
                  fab
                  icon
                  right
                  top
                  absolute
                  x-small
                  class="mt-3 mr-n5"
                  style="top: -20px"
                  @click="shareMe = false"
                >
                  <VIcon>
                    mdi-close
                  </VIcon>
                </VBtn>
                <div v-if="candidate.publicToken && !publicTokenLoading">
                  <span v-if="linkCopied">
                    <VIcon color="success">mdi-check</VIcon>
                    {{ $t('linkCopied') }}
                  </span>
                  <div>{{ candidate.publicToken.url }}</div>
                  <span class="blue--text cursor-pointer body-2" @click="copyLink">
                    {{ $t('copyLink') }}
                  </span>
                  <VIcon @click="refreshCandidateUrl">
                    mdi-refresh
                  </VIcon>
                </div>
                <Preloader v-else />
              </VCard>
            </VMenu>
          </VCol>
        </VRow>
      </VCol>
    </VRow>

    <VRow v-else>
      <VCol
        cols="12"
        md="7"
        lg="8"
        class="pb-0"
      >
        <VTextField
          v-model="edit.firstName"
          outlined
          dense
          :label="$t('firstName')"
          autocomplete="given-name"
          :hide-details="!errors.firstName"
          class="body-2 mb-3 mt-3 mt-md-0 denseinput"
          :error-messages="errors.firstName"
          @input="errors.firstName = null"
        />

        <VTextField
          v-model="edit.lastName"
          outlined
          dense
          :label="$t('lastName')"
          autocomplete="family-name"
          :hide-details="!errors.lastName"
          class="body-2 mb-3 denseinput"
          :error-messages="errors.lastName"
          @input="errors.lastName = null"
        />

        <VMenu
          v-model="birthDateMenu"
          :close-on-content-click="false"
          :nudge-right="40"
          transition="scale-transition"
          offset-y
          min-width="290px"
        >
          <template v-slot:activator="{ on: { click } }">
            <VTextField
              v-mask="birthDateMask"
              :value="birthDate"
              :label="$t('birthDate')"
              single-line
              clearable
              outlined
              dense
              :hide-details="!errors.birthDate"
              class="body-2 mb-3 denseinput"
              :error-messages="errors.birthDate"
              :rules="[validateBirthDate]"
              :prepend-inner-icon="calendarIcon"
              :validate-on-blur="true"
              @click="click"
              @change="birthDate = $event"
            />
          </template>
          <VDatePicker
            ref="birthDatePicker"
            v-model="edit.birthDate"
            :max="new Date().toISOString().substr(0, 10)"
            min="1920-01-01"
            :first-day-of-week="1"
            :locale="$i18n.locale"
            @input="birthDateMenu = false"
          />
        </VMenu>
        <CandidateStatusPanel class="mb-3 denseinput" :candidate="candidate" :readonly="readonly" />
        <VTextField
          v-model="edit.location"
          outlined
          dense
          :label="$t('city')"
          :hide-details="!errors.location"
          class="body-2 mb-3 denseinput"
          :error-messages="errors.location"
          @input="errors.location = null"
        />

        <VTextField
          v-model="edit.email"
          outlined
          dense
          type="email"
          :label="$t('email')"
          :hide-details="!errors.email"
          class="body-2 mb-3 denseinput"
          :error-messages="errors.email"
          @input="errors.email = null"
        />

        <PhoneNumberInput
          v-model="edit.phone"
          required
          :label="$t('phone')"
          :error-messages="errors.phone"
        />
      </VCol>
      <VCol
        cols="12"
        md="5"
        lg="4"
        class="pb-3 d-flex flex-column justify-end"
      >
        <CandidateRating
          v-if="$vuetify.breakpoint.mdAndUp"
          :candidate="candidate"
          :readonly="readonly"
          :align="'align-end'"
          class="mb-auto"
          description
          size="16"
        />
        <Preloader v-if="resumeLoading" class="flex-grow-0 mt-auto pb-4" />
        <template v-else>
          <VCard
            v-if="!candidate.resume"
            outlined
            class="d-flex flex-column full-width justify-center align-center body-2 cursor-pointer"
            height="108"
            @click="$refs['upload-resume'].$refs.input.click()"
          >
            <VIcon class="mb-2">
              M4.1,2.4h9v3h3v9h2v-10l-4-4h-10C3.5,0.4,3,0.6,2.6,1C2.3,1.4,2.1,
              1.9,2.1,2.4v16c0,0.5,0.2,1,0.6,1.4 s0.9,0.6,1.4,0.6h8v-2h-8C4.1,
              18.4,4.1,2.4,4.1,2.4z M14.1,9.4h-8v2h8V9.4z M14.1,13.4h-8v2h8V13.4z
              M16,15.5h2v3h3v2h-3v3h-2v-3h-3 v-2h3V15.5z
            </VIcon>
            <span>
              {{ $t('uploadResume') }}
            </span>
            <VFileInput
              ref="upload-resume"
              style="display: none"
              accept=".doc,.docx,.pdf,.odt,.rtf"
              @change="uploadResume"
            />
          </VCard>
          <VCard
            v-if="candidate.resume"
            outlined
            class="body-2"
          >
            <div
              class="d-flex justify-start align-center my-5 cursor-pointer"
              @click="downloadResume"
            >
              <VIcon small class="ml-2 mr-1">
                mdi-file-document-outline
              </VIcon>
              <span class="grey--text darken-4--text">
                {{ $t('downloadResume') }}
              </span>
            </div>
            <VDivider />
            <VCardActions @click="deleteResume">
              <div class="d-flex justify-start align-center cursor-pointer">
                <VIcon small class="mr-1" color="error">
                  mdi-delete
                </VIcon>
                <span class="grey--text darken-5--text">
                  {{ $t('deleteResume') }}
                </span>
              </div>
            </VCardActions>
          </VCard>
        </template>
      </VCol>

      <VCol
        cols="12"
        md="7"
        lg="8"
        class="py-md-0"
      >
        <VBtn
          small
          color="success"
          :loading="editLoading"
          :width="$vuetify.breakpoint.smAndDown ? '100%' : ''"
          class="mr-2"
          elevation="0"
          @click="updateCandidate"
        >
          {{ $t('save') }}
        </VBtn>
        <VBtn
          v-if="$vuetify.breakpoint.mdAndUp"
          small
          color="white elevation-0 grey--text"
          @click="cancelEdit"
        >
          {{ $t('cancel') }}
        </VBtn>
      </VCol>
      <VCol
        cols="12"
        md="5"
        lg="4"
        class="py-md-0 d-flex justify-space-between"
      >
        <VBtn
          v-if="$vuetify.breakpoint.smAndDown"
          small
          text
          color="grey"
          @click="cancelEdit"
        >
          {{ $t('cancel') }}
        </VBtn>
        <Confirm
          v-if="!readonly"
          :title="$t('candidateDeletion')"
          :description="$t('candidateDeletionTip')"
          :cancel-text="$t('cancel')"
          :confirm-text="$t('delete')"
          :max-width="500"
          @confirmed="deleteCandidate"
        >
          <template #activator="{ on: { click }}">
            <VBtn
              small
              color="ml-n3 white elevation-0 grey--text"
              :loading="deleteLoading"
              @click="click"
            >
              <VIcon class="mr-2" color="error">
                mdi-account-remove
              </VIcon>
              <span class="grey--text darken-5--text">
                {{ $t('deleteCandidate') }}
              </span>
            </VBtn>
          </template>
        </Confirm>
      </VCol>
    </VRow>
    <template v-show="false">
      <a :href="whatsAppApplicationUrl" ref="whatsAppApplicationLink" />
      <a :href="whatsAppUrl" target="_blank" ref="whatsAppLink" />
    </template>
  </VForm>
</template>

<script>
import { mask } from 'vue-the-mask';
import { format, parse } from 'date-fns';
import { mdiCalendar } from '@mdi/js';
import copy from 'clipboard-copy';
import PhoneNumberInput from '@/components/Input/PhoneNumberInput';
import { normalizationData } from '@/helpers/forms';
import useAccount from '@/compositions/accounting/useAccount';
import { computed, ref, toRefs } from '@vue/composition-api';
import useSubscription from '@/compositions/accounting/useSubscription';
import { paymentTabs, settingsTabs, vacancyStatus } from '@/enums';
import ValidationMixin from '../../mixins/ValidationMixin';
import CandidateLocationAndYearsOld from './CandidateLocationAndYearsOld.vue';
import CandidateStatusPanel from './CandidateStatusPanel.vue';
import CandidateRating from './CandidateRating.vue';
import Confirm from '../Confirm.vue';
import { DATE_FORMATS, DATE_MASKS } from '../../date-utils';
import Candidate from '../../orm/models/Candidate/Candidate';
import Resume from '../../orm/models/Candidate/Resume';
import PublicToken from '../../orm/models/Candidate/PublicToken';
import Preloader from '../Preloader.vue';
import { normalizePhoneNumber, objectToFormData } from '../../functions';

export default {
  name: 'CandidateHeader',
  components: {
    PhoneNumberInput,
    CandidateLocationAndYearsOld,
    Confirm,
    CandidateRating,
    CandidateStatusPanel,
    Preloader,
  },
  directives: { mask },
  mixins: [ValidationMixin],
  props: {
    candidate: {
      type: Object,
      required: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    vacancy: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    const { candidate, vacancy } = toRefs(props);
    const { account } = useAccount();
    const isReadFull = ref(false);
    const { isExpired } = useSubscription();
    const isAiRating = computed(() => (candidate.value?.interview?.aiRating === 0 ? true : !!candidate.value?.interview?.aiRating));
    const isAiReview = computed(() => !!candidate.value?.interview?.aiReview);
    const isAvailableAi = computed(() => !isAiRating.value && !isAiReview.value && account.value?.aiReview);
    const isNotAvailableAi = computed(() => !account.value?.aiReview);
    const transcriptions = computed(() => candidate.value?.interview?.timestamps?.some((i) => i.transcription !== null));
    const candidateInterviewed = computed(() => !!candidate.value?.interview?.video);

    const isProcessingAi = computed(() => account.value?.aiReview && candidateInterviewed.value
      && (!isAiRating.value || !isAiReview.value || !transcriptions.value));

    const ourDate = new Date();
    const published = new Date(vacancy.value.publishedTill);

    const hideBlockRatingComputed = computed(() => (vacancy.value.status === vacancyStatus.ARCHIVE || ourDate > published)
      && !account.value?.aiReview);

    const candidateRating = computed(() => (candidate.value?.interview?.aiRating === 0 ? candidate.value?.interview?.aiRating + 0.5
      : candidate.value?.interview?.aiRating / 2));

    const candidateRatingNull = computed(() => isAiRating.value && isAiReview.value
      && transcriptions.value && candidate.value?.interview?.aiRating === 0);
    const isAiRatingAvailable = computed(() => (isAiRating.value && isAiReview.value && transcriptions.value) || candidateRatingNull.value);

    return {
      account,
      isAiRating,
      isAiReview,
      isReadFull,
      isAvailableAi,
      isNotAvailableAi,
      isProcessingAi,
      isExpired,
      paymentTabs,
      settingsTabs,
      vacancyStatus,
      hideBlockRatingComputed,
      candidateRating,
      candidateRatingNull,
      transcriptions,
      isAiRatingAvailable,
    };
  },
  data() {
    return {
      isEditMode: false,
      birthDateMenu: null,
      shareMe: false,
      edit: {
        firstName: null,
        lastName: null,
        location: null,
        birthDate: null,
        email: null,
        phone: null,
      },
      editLoading: false,
      deleteLoading: false,
      calendarIcon: mdiCalendar,
      linkCopied: false,
      resumeLoading: false,
      publicTokenLoading: false,
      blurEventListener: null,
    };
  },
  computed: {
    hhStateName() {
      const hhNegotiationData = this.candidate?.hhNegotiationData
        || this.candidate?.hhIntegration?.hhNegotiationData;
      return hhNegotiationData?.employer_state?.name
        || hhNegotiationData?.state?.name
        || '';
    },
    birthDate: {
      get() {
        const birthDate = this.edit?.birthDate || this.candidate?.birthDate;
        if (!birthDate) {
          return null;
        }

        return format(
          parse(birthDate, DATE_FORMATS.server.short, new Date()),
          DATE_FORMATS[this.$i18n.locale].short,
        );
      },
      set(value) {
        if (this.validateBirthDate(value) !== true) {
          return;
        }

        if (value === null) {
          this.edit.birthDate = null;
          return;
        }

        this.edit.birthDate = format(
          parse(value, DATE_FORMATS[this.$i18n.locale].short, new Date()),
          DATE_FORMATS.server.short,
        );
      },
    },
    opened() {
      return !!this.candidate?.interview?.opened;
    },
    birthDateMask() {
      return DATE_MASKS[this.$i18n.locale].short;
    },
    whatsAppUrl() {
      return `https://api.whatsapp.com/send/?phone=${this.getClearPhone(this.candidate.phone)}`;
    },
    whatsAppApplicationUrl() {
      return `whatsapp://send?phone=${this.getClearPhone(this.candidate.phone)}`;
    },
  },
  watch: {
    birthDateMenu(val) {
      if (val) {
        setTimeout(() => {
          this.$refs.birthDatePicker.activePicker = 'YEAR';
        });
      }
    },
    shareMe(val) {
      if (val) {
        this.linkCopied = false;
      }
    },
  },
  methods: {
    switchToEditMode() {
      this.edit.firstName = this.candidate.firstName;
      this.edit.lastName = this.candidate.lastName;
      this.edit.location = this.candidate.location;
      this.edit.birthDate = this.candidate.birthDate;
      this.edit.email = this.candidate.email;
      this.edit.phone = this.candidate.phone;

      this.isEditMode = true;
    },
    async updateCandidate() {
      await this.withValidationHandling(async () => {
        await Candidate.api().update(
          this.candidate.id,
          normalizationData(this.edit, { phone: normalizePhoneNumber }),
        );
        this.isEditMode = false;
      }, 'editLoading');
    },
    cancelEdit() {
      this.edit.firstName = null;
      this.edit.lastName = null;
      this.edit.location = null;
      this.edit.birthDate = null;
      this.edit.email = null;
      this.edit.phone = null;

      this.isEditMode = false;
    },
    deleteCandidate() {
      this.withApiErrorHandling(async () => {
        await Candidate.api().destroy(this.candidate.id);
        await this.$router.push({ name: 'candidates', params: { id: this.candidate.vacancyId } });
        await Candidate.delete(this.candidate.id);
      }, (error) => {
        this.$toast.error(error.message);
      }, 'deleteLoading');
    },
    validateBirthDate(value) {
      if (value === null) {
        return true;
      }
      if (value.length !== DATE_FORMATS[this.$i18n.locale].short.length) {
        return this.$t('dateNotCorrect');
      }
      try {
        format(
          parse(value, DATE_FORMATS[this.$i18n.locale].short, new Date()),
          DATE_FORMATS.server.short,
        );
        return true;
      } catch (e) {
        return this.$t('dateNotCorrect');
      }
    },
    async generateCandidateUrl() {
      if (this.candidate.publicToken) {
        return;
      }
      this.withApiErrorHandling(async () => {
        await PublicToken.api().generate(this.candidate.id);
      }, (error) => {
        this.$toast.error(error.message);
      }, 'publicTokenLoading');
    },
    async refreshCandidateUrl() {
      this.withApiErrorHandling(async () => {
        await PublicToken.api().generate(this.candidate.id);
      }, (error) => {
        this.$toast.error(error.message);
      }, 'publicTokenLoading');
      this.linkCopied = false;
    },
    copyLink() {
      copy(this.candidate.publicToken.url);
      this.linkCopied = true;
    },
    async uploadResume(resume) {
      await this.withValidationHandling(async () => {
        await Resume.api().store(this.candidate.id, objectToFormData({ resume }));
      }, 'resumeLoading');
    },
    async deleteResume() {
      await this.withValidationHandling(async () => {
        await Resume.api().destroy(this.candidate.resume.id);
      }, 'resumeLoading');
    },
    downloadResume() {
      window.open(this.candidate.resume.file.url);
    },
    getClearPhone(phone) {
      if (!phone) {
        return '';
      }

      return phone.replace(/^\D+/g, '');
    },
    async openWhatsApp() {
      const hasWhatsApp = await this.detectWhatsApp(this.getClearPhone(this.candidate.phone));
      window.removeEventListener('blur', this.blurEventListener);

      if (hasWhatsApp) {
        this.$refs.whatsAppApplicationLink.click();
      } else {
        this.$refs.whatsAppLink.click();
      }
    },
    detectWhatsApp(phone) {
      const uri = `whatsapp://send/?phone=${encodeURIComponent(
        phone,
      )}}`;

      const onIE = () => {
        return new Promise((resolve) => {
          window.navigator.msLaunchUri(
            uri,
            () => resolve(true),
            () => resolve(false),
          );
        });
      };

      const notOnIE = () => {
        return new Promise((resolve) => {
          const a = document.getElementById('wapp-launcher') || document.createElement('a');
          a.id = 'wapp-launcher';
          a.href = uri;
          a.style.display = 'none';
          document.body.appendChild(a);

          const start = Date.now();
          const timeoutToken = setTimeout(() => {
            if (Date.now() - start > 1250) {
              resolve(true);
            } else {
              resolve(false);
            }
          }, 1000);

          const handleBlur = () => {
            clearTimeout(timeoutToken);
            resolve(true);
          };
          this.blurEventListener = handleBlur;
          window.addEventListener('blur', handleBlur);

          a.click();
        });
      };

      return window.navigator.msLaunchUri ? onIE() : notOnIE();
    },
  },
};
</script>

<style lang="scss" scoped>
.prepend {
  padding-top: 6px;
  width: 110px;
}

.denseinput {
  &::v-deep {
    .v-input__control {
      height: 32px;

      .v-input__slot {
        min-height: 32px !important;
        padding-right: 6px !important;

        .v-input__prepend-inner,
        .v-input__append-inner {
          margin-top: 4px !important;
        }
      }

      .v-label {
        font-size: 14px;
        top: 6px;

        &.v-label--active {
          top: 10px;
        }
      }
    }
  }

  &.error--text::v-deep {
    .v-input__control {
      height: auto;
    }
  }
}

.candidate-name-row {
  display: inline-flex;
  align-items: center;
}

.v-menu__content::v-deep {
  background-color: #fff;
  max-width: 370px;
  padding: 15px;
  color: #212121;
  font-size: 14px;
  font-weight: 300;
  line-height: 130%;
}

.error {
  &__title {
    font-size: 14px;
    font-weight: 300;
    line-height: normal;
  }

  &__trial {
    box-shadow: none !important;

    &_icon {
      @media screen and (max-width: 600px) {
        position: static;
        margin-bottom: 15px;
      }
    }

    &_close {
      margin: -7px -7px 0 0;
    }

    &_links {
      display: flex;
      align-items: center;
      @media screen and (max-width: 1265px) {
        margin-top: 10px;
      }
      @media screen and (max-width: 600px) {
        justify-content: space-between;
        margin-top: 20px;
      }
    }

    .try-days-block {
      margin-right: 25px;
    }

    .change-tariff-block {
      white-space: nowrap;
    }

    .button-trial {
      color: #0A82D9;
      font-size: 14px;
      font-weight: 400;
      line-height: normal;
      text-decoration-line: underline;
      white-space: nowrap;
    }

    .button-tariff {
      color: #212121;
      font-size: 14px;
      font-weight: 400;
      line-height: normal;
      text-decoration-line: underline;
      white-space: nowrap;
    }
  }
}

.ai-text-rating {
  color: #757575;
  text-align: end;
  font-size: 13px;
  font-weight: 400;
  line-height: 21px;
  white-space: nowrap;

  span {
    color: #448AFF;
    font-style: normal;
    font-weight: 600;
  }
}

.ai-text-review {
  color: #757575;
  font-size: 13px;
  text-align: start;
  font-weight: 400;
  line-height: 21px;
  white-space: nowrap;
}

.icon-more-detailed {
  cursor: pointer;
  margin-left: 6px;

  svg {
    &:focus {
      border: none;
      outline: none;
    }
  }
}
</style>

<i18n locale="en" lang="yaml">
downloadResume: Download resume
resume: Resume
sendToCollegue: Share with a colleague
send: Send
linkCopied: Link copied
copyLink: Copy link
firstName: First name
lastName: Last name
birthDate: Date of birth
city: City
email: E-mail
phone: Phone
uploadResume: Upload resume
deleteResume: Delete resume
save: Save
cancel: Cancel
delete: Remove
candidateDeletion: Remove candidate
candidateDeletionTip: Removing this candidate will delete the recorded interview
deleteCandidate: Delete candidate
dateNotCorrect: Incorrect date
evAiIsProcessing: Evaluation from AI in processing
availableAi: Evaluation from AI is available
notAvailableAi: Evaluation of the candidate from AI
accordingAi: points based on AI
textTooltipAi: {
  1: This candidate has been evaluated with the assistance of artificial intelligence (AI).,
  2: 'This evaluation will help you in choosing the best candidate. The candidate’s rating is based on a five-point scale and strict parameters,
    which will allow you to define the pluses and minuses of a candidate in the most maximally accurate way.'
}
conversionIsAvailable: AI Evaluation is available at the tariff “standard” and higher
free: 30 days for free
changeTariff: Change your tariff
payTheTariff: Pay the tariff
isExpiredTrialTitle: Your tariff is inactive
hhStateLabel: HH status
</i18n>

<i18n locale="ru" lang="yaml">
downloadResume: Скачать резюме
resume: Резюме
sendToCollegue: Отправить коллеге
send: Отправить
linkCopied: Ссылка скопирована
copyLink: Скопировать ссылку
firstName: Имя
lastName: Фамилия
birthDate: Дата рождения
city: Город
email: Эл. почта
phone: Телефон
uploadResume: Добавить резюме
deleteResume: Удалить резюме
save: Сохранить
cancel: Отменить
delete: Удалить
candidateDeletion: Удаление кандидата
candidateDeletionTip: Если вы удалите кандидата, то потеряете записанное интервью
deleteCandidate: Удалить кандидата
dateNotCorrect: Неверная дата
evAiIsProcessing: Оценка от ИИ в обработке!
availableAi: Доступна оценка от ИИ
notAvailableAi: Оценка кандидата от ИИ
accordingAi: по оценке ИИ
textTooltipAi: {
  1: Данный кандидат был оценен со стороны Искусственного Интеллекта.,
  2: 'Данная оценка поможет Вам в выборе кандидата.
    Оценка проводится по пятибалльной шкале и по строгим параметрам, что позволяет максимально точно определить все плюсы и минусы кандидата.'
}
conversionIsAvailable: Оценка кандидата от ИИ доступна на тарифе «Стандарт» и выше
free: Бесплатно 30 дн.
changeTariff: Сменить тариф
payTheTariff: Оплатить тариф
isExpiredTrialTitle: Тариф закончился
hhStateLabel: Статус hh.ru
</i18n>
